import React from 'react';
import { MentionCard } from './MentionCard';
import {
  setPublicity,
  setHiddenness,
  getMentions,
  Meta as OriginalMeta,
} from '../async_utils';
// @ts-expect-error Hasn't been migrated to TS yet
import { WaypointLoader } from '../../user_cards/components/waypoint-loader';
import { Spinner } from 'js/academia_components/transients/spinner';
import { $, $r } from 'global';
import { Error } from './Error';
import { logError, MentionsFetchError } from '../error';
import { Action, Mention, Filter } from '../common';
import FeaturedMention from './FeaturedMention';
import UpsellTestCard from './UpsellTestCard';
import TasteOfMentionsHeader from './TasteOfMentionsHeader';
import TasteOfMentionsUpsellCard from './TasteOfMentionsUpsellCard';
import TasteOfMentionsUpsellModal from './TasteOfMentionsUpsellModal';
import { MentionsCardHeader } from './MentionsCardHeader';

const routeActions = ({ id, citation, action }: Action) => {
  switch (action) {
    case 'is_me':
      return setPublicity({ id, citation, is_public: true });
    case 'undo_is_me':
      return setPublicity({ id, citation, is_public: false });
    case 'not_me':
      return setHiddenness({ is_hidden: true, id, citation });
    case 'undo_not_me':
      return setHiddenness({ is_hidden: false, id, citation });
  }
};

interface Props {
  onCountChange: (counts: Record<string, number>, sum: boolean) => void;
  currentFilter: Filter;
  mentionedUserId: number;
  mentions: Mention[];
  publicMentions?: boolean;
  counts: Record<string, number>;
  isFeaturedCitationContext?: boolean;
  isTasteOfMentions?: boolean;
  isTasteOfMentionsAutoclosing?: boolean;
  isBlurred?: boolean;
  pricingCopy?: string;
  annualPrice?: string;
  sortBy?: 'recency' | 'relevance';
  onSortChange?: (sortBy: 'recency' | 'relevance') => void;
  names?: string[];
  mentionedUserUrl?: string;
}

interface Meta extends OriginalMeta {
  fetching: boolean;
  error?: boolean;
  exhausted: boolean;
  filter?: Filter;
}

interface State {
  loading: boolean;
  mentions: Mention[];
  overrides: Record<number, object>;
  meta: Meta;
  showTasteOfMentionsUpsellModal: boolean;
}

const defaultState: State = {
  loading: true,
  mentions: [],
  meta: {
    fetching: false,
    error: false,
    exhausted: false,
    recent_view_counts: {},
    certification_counts: {},
    counts: {
      all: 0,
      high_confidence: 0,
      is_me: 0,
      not_me: 0,
    },
  },
  overrides: {},
  showTasteOfMentionsUpsellModal: false,
};

export class MentionsCardsContainer extends React.Component<Props, State> {
  static defaultProps = {
    onCountChange: () => {},
  };

  state = defaultState;

  componentDidMount() {
    this.getCardData(this.props.currentFilter, this.props.mentionedUserId);
    if (this.props.isTasteOfMentionsAutoclosing) {
      this.setShowTasteOfMentionsUpsellModal(true);
    }
  }

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'nextProps' implicitly has an 'any' type... Remove this comment to see the full error message
  componentWillUpdate(nextProps) {
    if (
      nextProps.currentFilter !== this.props.currentFilter ||
      nextProps.sortBy !== this.props.sortBy
    ) {
      this.setState(
        () => defaultState,
        () =>
          this.getCardData(nextProps.currentFilter, nextProps.mentionedUserId),
      );
    }
  }

  getCardData = async (
    currentFilter: Filter = 'all',
    mentionedUserId: number,
  ) => {
    // Uses mentions from props instead of loading them
    // TODO: Remove this or move to separate component, it's only used in
    // app/views/admin_content/compare_mentions.html.slim
    if (this.props.mentions !== undefined) {
      this.setState({
        mentions: this.props.mentions.map((m, i) => ({
          ...m,
          id: i,
        })),
        meta: {
          certification_counts: {},
          recent_view_counts: {},
          exhausted: true,
          filter: currentFilter,
          fetching: false,
          error: false,
          counts: {
            all: 0,
            high_confidence: 0,
            is_me: 0,
            not_me: 0,
          },
        },
      });
      return;
    }
    const { cursor_id: cursorId, exhausted } = this.state.meta;
    if (currentFilter === this.state.meta.filter && exhausted) return;

    this.setState((state) => ({ meta: { ...state.meta, fetching: true } }));

    const skipCounts = !this.props.publicMentions;
    try {
      const { mentions, meta } = await getMentions({
        currentFilter,
        cursorId,
        mentionedUserId,
        skipCounts,
        sortBy: this.props.sortBy,
      });
      this.setState((state) => ({
        mentions: [...state.mentions, ...mentions],
        meta: {
          ...meta,
          exhausted: !mentions.length,
          filter: currentFilter,
          fetching: false,
          recent_view_counts: {
            ...state.meta.recent_view_counts,
            ...meta.recent_view_counts,
          },
          certification_counts: {
            ...state.meta.certification_counts,
            ...meta.certification_counts,
          },
          section_title: {
            ...state.meta.section_title,
            ...meta.section_title,
          },
        },
      }));
    } catch (e) {
      if (!(e instanceof MentionsFetchError)) {
        // hasn't already been logged
        logError((e as any)?.toString());
      }
      this.setState((state) => ({
        meta: {
          ...state.meta,
          error: true,
          fetching: false,
        },
      }));
    }
  };

  handleOwnerAction = async ({ id, citation, action }: Action) => {
    const result = await routeActions({ id, citation, action });
    const { id: _id, ...attributes } = result;
    // override the privacy state without reloading page
    this.setState({
      overrides: {
        ...this.state.overrides,
        [id]: { ...this.state.overrides[id], ...attributes },
      },
    });

    // change the count for 'is_me and not_me'
    // avoids disabling those filters if falsely reported as 0
    if (attributes.is_public !== undefined) {
      this.props.onCountChange({ is_me: attributes.is_public ? 1 : -1 }, true);
    } else if (attributes.is_hidden !== undefined) {
      this.props.onCountChange({ not_me: attributes.is_hidden ? 1 : -1 }, true);
    }

    return result;
  };

  getHighlyCitedMentionCount() {
    $.get(
      $r.highly_cited_mentions_count_api_v0_mentions_path({
        subdomain_param: 'api',
      }),
      { visibility: 'public' },
    ).done(
      // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'response' implicitly has an 'any' type.
      function (response) {
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        if (this.state.loading && !response.pending) {
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          this.setState({
            highlyCitedMentionsCount: response.highly_cited_mentions_count,
          });
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          this.stopLoading();
        }
      }.bind(this),
    );
  }

  setShowTasteOfMentionsUpsellModal = (show: boolean) => {
    this.setState({ showTasteOfMentionsUpsellModal: show });
  };

  render() {
    const { state, props } = this;
    const { meta } = state;

    const { counts } = props.publicMentions ? meta : props;

    return (
      <div className="DesignSystem wp-card__containerSpacer">
        <MentionsCardHeader
          publicMentions={props.publicMentions}
          counts={counts}
          currentFilter={props.currentFilter}
          names={props.names}
          mentionedUserUrl={props.mentionedUserUrl}
          sortBy={props.sortBy}
          onSortChange={props.onSortChange}
        />
        {this.props.isTasteOfMentions && (
          <TasteOfMentionsHeader
            mentionsCount={this.props.counts ? this.props.counts.all : 0}
            onClickUpgrade={() => {
              this.setShowTasteOfMentionsUpsellModal(true);
            }}
          />
        )}
        <div className="mentions-card-container--cards">
          <FeaturedMention
            onAction={this.handleOwnerAction}
            hideLoading={meta.fetching}
            isFeaturedCitationContext={!!this.props.isFeaturedCitationContext}
          />

          {this.props.isFeaturedCitationContext && (
            <UpsellTestCard pricingCopy={this.props.pricingCopy || ''} />
          )}

          {state.mentions.map(this.renderMention)}
          <Spinner show={meta.fetching} />
          {meta.error && <Error />}
        </div>
        {this.state.showTasteOfMentionsUpsellModal && (
          <TasteOfMentionsUpsellModal
            onClose={() => {
              this.setShowTasteOfMentionsUpsellModal(false);
            }}
          />
        )}
      </div>
    );
  }

  renderMention = (mention: Mention, index: number) => {
    // if for any reason we don't have at least 5 mentions load more immediately
    const totalMentions = this.state.mentions.length;
    if (
      !(this.props.isFeaturedCitationContext || this.props.isTasteOfMentions) &&
      ((totalMentions > 5 && index === totalMentions - 4) ||
        (totalMentions <= 5 && index === 0))
    ) {
      return (
        <div key={mention.id}>
          <WaypointLoader
            fetching={this.state.meta.fetching}
            exhausted={this.state.meta.exhausted}
            fetcher={() =>
              this.getCardData(
                this.props.currentFilter,
                this.props.mentionedUserId,
              )
            }
          />
          {this.renderMentionCard(mention, index)}
        </div>
      );
    }

    return <div key={mention.id}>{this.renderMentionCard(mention, index)}</div>;
  };

  renderMentionCard = (mention: Mention, index: number) => {
    const { meta, overrides } = this.state;
    const isBlurred =
      !!this.props.isBlurred && (!this.props.isTasteOfMentions || index > 1);

    const shouldShowUpsell =
      index === 2 &&
      this.props.isTasteOfMentions &&
      !this.state.showTasteOfMentionsUpsellModal;

    return (
      <div style={{ position: 'relative' }}>
        <MentionCard
          publicMentions={!!this.props.publicMentions}
          {...mention}
          {...overrides[mention.id]}
          onAction={this.handleOwnerAction}
          certificationCount={
            meta.certification_counts[mention.mentioning_work_id]
          }
          recentViewCount={meta.recent_view_counts[mention.mentioning_work_id]}
          sectionTitle={meta.section_title?.[mention.id]}
          highlyCited={!!mention.highly_cited}
          wellKnownAuthor={!!mention.well_known_author}
          isFeaturedCitationContext={!!this.props.isFeaturedCitationContext}
          isBlurred={isBlurred}
          currentFilter={this.props.currentFilter}
          position={index}
          ordering_method={meta.ordering_method}
        />

        {shouldShowUpsell && (
          <TasteOfMentionsUpsellCard
            mentionsCount={this.props.counts ? this.props.counts.all : 0}
            annualPrice={this.props.annualPrice || ''}
            onClickUpgrade={() => {
              this.setShowTasteOfMentionsUpsellModal(true);
            }}
          />
        )}
      </div>
    );
  };
}
